import { forwardRef, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, CSSObject } from '@emotion/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  Placement,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { isEqual } from 'lodash-es';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  Button,
  CaretDownIconSmall,
  CheckboxField,
  ChecklistField,
  ChecklistMenuField,
  Chip,
  FieldLayout,
  FieldLayoutWithAction,
  ListFieldProps,
  PopoverMenu,
  useControlledField,
  useOnClickOutside,
  usePopoverMenu,
} from '@frontend/design-system';

const FLOATING_MENU_MAX_HEIGHT = 400;
const FLOATING_MENU_MIN_WIDTH = 220;

type GroupedChildOption = {
  disabled?: boolean;
  label: ReactNode;
  labelText?: string;
  value: string;
};

type GroupOption = {
  children: GroupedChildOption[];
  hideLabel?: boolean;
  labelText?: string;
  label: ReactNode;
  value: string;
};

type GroupedCheckListDropdownMenuProps<T extends string = string> = {
  disabled?: boolean;
  floatingStyleOverrides?: React.CSSProperties;
  groups: GroupOption[];
  label: string;
  name: string;
  onChange: (selected: T[]) => void;
  placement?: Placement;
  placeholder?: string;
  showLabel?: boolean;
  trackingIdBase?: string;
  value?: T[];
};

type GroupSelectionState = {
  allSelected: boolean;
  hasChildren: boolean;
  isIndeterminate: boolean;
  selectedCount: number;
  totalCount: number;
};

type GroupCheckboxProps = {
  disabled?: boolean;
  indeterminate: boolean;
  isChecked: boolean;
  label: ReactNode;
  name: string;
  onChange: (value: boolean) => void;
  trackingId?: string;
};

type ChipInputFieldWrapperProps = {
  keyToLabelMap: Record<string, string>;
  onRemoveClick: (chipValue: string) => void;
  selectedKeys: string[];
};

type ChipDropdownMenuCustomStyles = {
  applyButton?: CSSObject;
  checkListWrapper?: CSSObject;
  chipDropdownFilter?: CSSObject;
  popoverMenuWrapper?: CSSObject;
  topBar?: CSSObject;
};

type GroupedChecklistChipDropdownMenuProps<T extends string = string> = {
  customStyles?: ChipDropdownMenuCustomStyles;
  defaultValue?: T[];
  disabled?: boolean;
  groups: GroupOption[];
  label: string;
  onChange: (selected: T[]) => void;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
  value?: T[];
};

const GroupCheckbox = memo(
  ({ disabled, indeterminate, isChecked, label, name, onChange, trackingId }: GroupCheckboxProps) => {
    const checkboxFieldProps = useControlledField({
      onChange: (newValue: boolean) => {
        onChange(newValue);
      },
      type: 'checkbox',
      value: isChecked,
    });

    return (
      <CheckboxField
        {...checkboxFieldProps}
        css={styles.groupCheckbox}
        disabled={disabled}
        indeterminate={indeterminate}
        label={label}
        labelPlacement='right'
        name={name}
        trackingId={trackingId}
      />
    );
  }
);

GroupCheckbox.displayName = 'GroupCheckbox';

const ChipInputFieldWrapper = memo(
  forwardRef<HTMLDivElement, ChipInputFieldWrapperProps>(
    ({ selectedKeys, keyToLabelMap, onRemoveClick, ...props }, ref) => {
      return (
        <div ref={ref} {...props} style={styles.chipInputContainer}>
          <div css={styles.chipInputWrapper}>
            <ChecklistMenuField.ChipInput
              keyToLabelMap={keyToLabelMap}
              onRemoveClick={onRemoveClick}
              selectedKeys={selectedKeys}
              variant='primary'
            />
          </div>
        </div>
      );
    }
  )
);

ChipInputFieldWrapper.displayName = 'ChipInputFieldWrapper';

const GroupedChecklistContent = memo(
  ({
    checklistProps,
    disabled,
    groups,
    groupStates,
    label,
    name,
    onToggleGroup,
    showLabel,
    trackingIdBase,
  }: {
    checklistProps: ListFieldProps;
    disabled?: boolean;
    groups: GroupOption[];
    groupStates: Map<string, GroupSelectionState>;
    label: string;
    name: string;
    onToggleGroup: (group: GroupOption) => void;
    showLabel?: boolean;
    trackingIdBase?: string;
  }) => {
    return (
      <ChecklistField
        {...checklistProps}
        css={styles.checklist(showLabel)}
        disabled={disabled}
        label={label}
        name={name}
      >
        {groups.map((group) => {
          const state = groupStates.get(group.value);
          const hasChildren = group.children.length > 0;
          const hideGroupLabel = group.hideLabel ?? false;

          return (
            <div css={styles.group} key={group.value}>
              {!hideGroupLabel && (
                <div css={styles.groupHeader(hasChildren)}>
                  <GroupCheckbox
                    disabled={disabled}
                    indeterminate={state?.isIndeterminate ?? false}
                    isChecked={state?.isIndeterminate ? true : state?.allSelected ?? false}
                    label={group.label}
                    name={group.value}
                    onChange={() => onToggleGroup(group)}
                    trackingId={`${trackingIdBase}-option-${group.value.toLowerCase()}`}
                  />
                </div>
              )}

              {hasChildren && (
                <>
                  {group.children.map((child) => (
                    <div css={styles.childOption(hideGroupLabel)} key={child.value}>
                      <ChecklistField.Option
                        css={styles.childCheckbox}
                        disabled={child.disabled}
                        name={child.value}
                        trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
                      >
                        {child.label}
                      </ChecklistField.Option>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })}
      </ChecklistField>
    );
  }
);

GroupedChecklistContent.displayName = 'GroupedChecklistContent';

export function GroupedCheckListDropdownMenu<T extends string = string>({
  disabled,
  floatingStyleOverrides,
  groups,
  label,
  name,
  onChange,
  placement = 'bottom',
  placeholder = 'Select one',
  showLabel = false,
  trackingIdBase,
  value = [],
}: GroupedCheckListDropdownMenuProps<T>) {
  const [menuState, setMenuState] = useState({
    isOpen: false,
  });

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => {
      onChange?.(newValue as T[]);
    },
    type: 'checklist',
    value: value as string[],
  });

  const floatingNodeId = useFloatingNodeId();

  const floatingConfig = useMemo(
    () => ({
      middleware: [
        offset(placement === 'top' ? 12 : 3),
        flip(),
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
      nodeId: floatingNodeId,
      onOpenChange: (nextOpen: boolean) => {
        setMenuState({ isOpen: nextOpen });
      },
      open: menuState.isOpen,
      placement,
      whileElementsMounted: autoUpdate,
    }),
    [floatingNodeId, menuState.isOpen, placement]
  );

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>(floatingConfig);

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const groupSelectionStates = useMemo(() => {
    const states = new Map<string, GroupSelectionState>();

    groups.forEach((group) => {
      const hasChildren = group.children.length > 0;

      if (hasChildren) {
        const childValues = group.children.map((child) => child.value);
        const selectedCount = childValues.filter((childValue) => value.includes(childValue as T)).length;
        const totalCount = childValues.length;

        states.set(group.value, {
          allSelected: selectedCount === totalCount && totalCount > 0,
          hasChildren: true,
          isIndeterminate: selectedCount > 0 && selectedCount < totalCount,
          selectedCount,
          totalCount,
        });
      } else {
        const isGroupSelected = value.includes(group.value as T);

        states.set(group.value, {
          allSelected: isGroupSelected,
          hasChildren: false,
          isIndeterminate: false,
          selectedCount: isGroupSelected ? 1 : 0,
          totalCount: 1,
        });
      }
    });

    return states;
  }, [groups, value]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const parentValue = group.value as T;
        const isSelected = value.includes(parentValue);

        onChange(isSelected ? value.filter((v) => v !== parentValue) : [...value, parentValue]);
      } else {
        const enabledChildValues = group.children.filter((child) => !child.disabled).map((child) => child.value) as T[];

        if (state?.allSelected) {
          onChange(value.filter((v) => !enabledChildValues.includes(v)));
        } else {
          const updatedValue = [...value];
          enabledChildValues.forEach((childValue) => {
            if (!updatedValue.includes(childValue)) {
              updatedValue.push(childValue);
            }
          });
          onChange(updatedValue);
        }
      }
    },
    [groupSelectionStates, onChange, value]
  );

  const valueToLabelMap = useMemo(() => {
    const map: Record<string, string> = {};

    groups.forEach((group) => {
      if (group.children.length === 0) {
        map[group.value] = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
      } else {
        group.children.forEach((child) => {
          map[child.value] = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
        });
      }
    });

    return map;
  }, [groups]);

  const handleRemoveChip = useCallback(
    ({ chipValue }: { chipValue: string }) => {
      onChange(value.filter((v) => v !== chipValue) as T[]);
    },
    [onChange, value]
  );

  const chipInputFieldProps = useMemo(
    () => ({
      keyToLabelMap: valueToLabelMap,
      onRemoveClick: (chipValue: string) => handleRemoveChip({ chipValue }),
      selectedKeys: value as string[],
    }),
    [handleRemoveChip, value, valueToLabelMap]
  );

  const handleToggleMenu = useCallback(() => {
    setMenuState((prevState) => ({ isOpen: !prevState.isOpen }));
  }, []);

  const floatingMenuStyles = useMemo(
    () => ({
      ...floatingStyles,
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.heavy,
      display: 'grid',
      gridTemplateRows: 'minmax(0, 1fr)',
      maxHeight: FLOATING_MENU_MAX_HEIGHT,
      minWidth: FLOATING_MENU_MIN_WIDTH,
      overflow: 'auto',
      zIndex: theme.zIndex.popover,
      ...floatingStyleOverrides,
    }),
    [floatingStyles, floatingStyleOverrides]
  );

  return (
    <FieldLayout
      {...checklistFieldProps}
      {...getReferenceProps()}
      containerCss={styles.fieldContainer}
      endAdornment={
        <CaretDownIconSmall
          color='subdued'
          css={[
            css`
              cursor: pointer;
              transition: transform 250ms ease-out;
              pointer-events: ${menuState.isOpen ? 'none' : 'auto'};
            `,

            menuState.isOpen &&
              css`
                transform: rotate(180deg);
              `,
          ]}
          name='alt-caret-down-tiny'
          onClick={handleToggleMenu}
          size={16}
        />
      }
      field={ChipInputFieldWrapper as unknown as ComponentProps<typeof FieldLayoutWithAction>['field']}
      fieldComponentProps={chipInputFieldProps}
      hasPadding={false}
      label={label}
      name={name}
      onChange={() => {}}
      placeholder={placeholder}
      ref={refs.setReference}
      value={value}
      active={menuState.isOpen}
    >
      <FloatingNode id={floatingNodeId}>
        {menuState.isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={0}>
              <div style={floatingMenuStyles} ref={refs.setFloating}>
                <GroupedChecklistContent
                  checklistProps={checklistFieldProps}
                  disabled={disabled}
                  groups={groups}
                  groupStates={groupSelectionStates}
                  label={label}
                  name={name}
                  onToggleGroup={handleToggleGroupSelection}
                  showLabel={showLabel}
                  trackingIdBase={trackingIdBase}
                />
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </FieldLayout>
  );
}

export function GroupedChecklistChipDropdownMenu<T extends string = string>({
  customStyles,
  defaultValue = [],
  disabled,
  groups,
  label,
  onChange,
  renderIcon,
  trackingId,
  value,
}: GroupedChecklistChipDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');
  const { close, getMenuProps, getTriggerProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();
  const [internalSelection, setInternalSelection] = useState<T[]>(value || []);

  const outsideClickRef = useRef<HTMLDivElement>(null);
  const isFilterActive = !isEqual([...defaultValue].sort(), [...(value || [])].sort());

  const leftElement = useMemo(() => {
    return renderIcon ? renderIcon(isFilterActive, isOpen) : null;
  }, [renderIcon, isFilterActive, isOpen]);

  useEffect(() => {
    setInternalSelection(value || []);
  }, [value]);

  const handleCancelSelection = useCallback(() => {
    setInternalSelection(value || []);
    close();
  }, [close, value]);

  useOnClickOutside({
    handler: handleCancelSelection,
    ref: outsideClickRef,
  });
  const handleApplySelection = useCallback(() => {
    onChange(internalSelection);
    close();
  }, [close, internalSelection, onChange]);

  const groupSelectionStates = useMemo(() => {
    const states = new Map<string, GroupSelectionState>();

    groups.forEach((group) => {
      const hasChildren = group.children.length > 0;

      if (hasChildren) {
        const enabledChildren = group.children.filter((child) => !child.disabled).map((child) => child.value as T);
        const selectedChildrenCount = enabledChildren.filter((childValue) =>
          internalSelection.includes(childValue)
        ).length;
        const totalChildrenCount = enabledChildren.length;

        states.set(group.value, {
          allSelected: totalChildrenCount > 0 && selectedChildrenCount === totalChildrenCount,
          hasChildren: true,
          isIndeterminate: selectedChildrenCount > 0 && selectedChildrenCount < totalChildrenCount,
          selectedCount: selectedChildrenCount,
          totalCount: totalChildrenCount,
        });
      } else {
        const isGroupSelected = internalSelection.includes(group.value as T);

        states.set(group.value, {
          allSelected: isGroupSelected,
          hasChildren: false,
          isIndeterminate: false,
          selectedCount: isGroupSelected ? 1 : 0,
          totalCount: 1,
        });
      }
    });

    return states;
  }, [groups, internalSelection]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const groupValue = group.value as T;
        setInternalSelection((previousSelection) =>
          previousSelection.includes(groupValue)
            ? previousSelection.filter((selectedValue) => selectedValue !== groupValue)
            : [...previousSelection, groupValue]
        );
        return;
      }

      const enabledChildren = group.children.filter((child) => !child.disabled).map((child) => child.value as T);

      setInternalSelection((previousSelection) => {
        if (state.allSelected) {
          return previousSelection.filter((selectedValue) => !enabledChildren.includes(selectedValue));
        }

        const updatedSelection = [...previousSelection];
        enabledChildren.forEach((childValue) => {
          if (!updatedSelection.includes(childValue)) {
            updatedSelection.push(childValue);
          }
        });
        return updatedSelection;
      });
    },
    [groupSelectionStates]
  );

  const allSelectableValues = useMemo(() => {
    const selectableValues: T[] = [];

    groups.forEach((group) => {
      if (group.children.length === 0) {
        selectableValues.push(group.value as T);
        return;
      }

      group.children.forEach((child) => {
        if (!child.disabled) {
          selectableValues.push(child.value as T);
        }
      });
    });

    const uniqueValues = new Set<string>();
    return selectableValues.filter((selectableValue) => {
      if (uniqueValues.has(selectableValue)) {
        return false;
      }
      uniqueValues.add(selectableValue);
      return true;
    });
  }, [groups]);

  const handleSelectAll = useCallback(() => {
    setInternalSelection(allSelectableValues);
  }, [allSelectableValues]);

  const handleClearAll = useCallback(() => {
    setInternalSelection([]);
  }, []);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => setInternalSelection(newValue as T[]),
    type: 'checklist',
    value: internalSelection as string[],
  });

  return (
    <div ref={filterTriggerRef}>
      <Chip.DropdownFilter
        {...triggerProps}
        css={{
          gap: theme.spacing(1),
          maxWidth: 'none',
          ...customStyles?.chipDropdownFilter,
        }}
        filterIsActive={isFilterActive}
        leftElement={leftElement}
        onClick={open}
        trackingId={trackingId}
      >
        {label}
      </Chip.DropdownFilter>

      <PopoverMenu {...getMenuProps()}>
        <div css={[styles.checkListWrapper, customStyles?.popoverMenuWrapper]} ref={outsideClickRef}>
          <div css={[styles.topBar, customStyles?.topBar]}>
            <Button onClick={handleSelectAll} variant='tertiary' disabled={disabled}>
              {t('Select all')}
            </Button>
            <Button onClick={handleClearAll} variant='tertiary' disabled={disabled}>
              {t('Clear')}
            </Button>
          </div>

          <div css={customStyles?.checkListWrapper}>
            <GroupedChecklistContent
              checklistProps={checklistFieldProps}
              disabled={disabled}
              groups={groups}
              groupStates={groupSelectionStates}
              label={label}
              name={label}
              onToggleGroup={handleToggleGroupSelection}
              showLabel={false}
              trackingIdBase={trackingId}
            />
          </div>

          <Button
            css={[styles.applyButton, customStyles?.applyButton]}
            disabled={disabled}
            onClick={handleApplySelection}
            trackingId={trackingId ? `${trackingId}-apply-button` : undefined}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverMenu>
    </div>
  );
}

const styles = {
  checklist: (showLabel?: boolean) => css`
    > label {
      display: ${showLabel ? 'block' : 'none'};
    }

    > div {
      align-items: center;
    }

    > div:last-of-type {
      margin-bottom: 0;
    }
  `,

  childCheckbox: css`
    margin: 0;
  `,

  childOption: (hideGroupLabel?: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${hideGroupLabel ? theme.spacing(1, 0) : theme.spacing(1, 1, 1, 4)};

    > div {
      width: 100%;

      > label {
        width: 100%;
      }
    }
  `,

  group: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(1, 2)};
    &:last-of-type {
      border-bottom: none;
    }
  `,

  groupCheckbox: css`
    margin: 0;
    align-items: center;
    width: 100%;
    > label {
      width: 100%;
    }
  `,

  groupCheckboxOption: css`
    margin: 0;
    > label {
      display: none;
    }
  `,

  groupHeader: (hasChildren: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(hasChildren ? 1 : 0)};
  `,

  chipInputContainer: {
    height: '100%',
    width: '100%',
  },

  chipInputWrapper: css`
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .chip {
      background-color: ${theme.colors.primary5};
      color: ${theme.font.colors.default};

      svg {
        color: ${theme.colors.primary90};
      }
    }
  `,

  fieldContainer: css`
    > div {
      height: 40px;
      padding: ${theme.spacing(1, 1)};
    }
  `,
  /******* */

  topBar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(0.25, 2)};

    button {
      color: ${theme.colors.text.interactive};
    }
  `,
  checkListWrapper: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-height: 400px;
    min-width: 220px;
  `,
  applyButton: css`
    margin: auto;
    width: calc(100% - ${theme.spacing(2)});
  `,
};
